<template>
    <div
        id="loader"
        :class="{visible: isAnyProcessing, 'fade-out': fadeOut}"
    >
        <div class="backdrop" :class="{fullscreen}" />
        <div
            class="content"
            :class="{fullscreen, 'page-loaded': isPageLoaded, 'roster-loaded': isRosterLoaded}"
            ref="contentElement"
            @transitionend="transitionEnd"
        >
            <div class="header">Bezig met laden...</div>
            <div @click="closeModal" class="closeButton">X</div>
            <div class="inner-content">
                <div v-if="isStateProcessing(loaderStates.rosters)" class="row rosters">
                    <i class="icon" :class="stateIconClass(loaderStates.rosters)" />
                    Roosters
                </div>
                <div v-if="isStateProcessing(loaderStates.shifts)" class="row shifts">
                    <i class="icon" :class="stateIconClass(loaderStates.shifts)" />
                    Dienstcodes
                </div>
                <div v-if="isStateProcessing(loaderStates.configuration)" class="row configuration">
                    <i class="icon" :class="stateIconClass(loaderStates.configuration)" />
                    Configuratie
                </div>
                <div v-if="isStateProcessing(loaderStates.roster)" class="row roster">
                    <i class="icon" :class="stateIconClass(loaderStates.roster)" />
                    Rooster {{ rosterName }}
                </div>
                <div v-if="isStateProcessing(loaderStates.hourCounter)" class="row hour-counter">
                    <i class="icon" :class="stateIconClass(loaderStates.hourCounter)" />
                    Urentelling
                </div>
                <div v-if="isStateProcessing(loaderStates.resources)" class="row hour-counter">
                    <i class="icon" :class="stateIconClass(loaderStates.resources)" />
                    Medewerkers
                </div>
                <div v-if="isStateProcessing(loaderStates.workSchedule)" class="row position-assignment">
                    <i class="icon" :class="stateIconClass(loaderStates.workSchedule)" />
                    Positietoewijzing
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Ref, Watch, Prop} from 'vue-facing-decorator';
    import {LoadingStates, LoadingCounters, LoadingCategory} from '@/types/types';
    import {LoadingState} from '@/types/enums/LoadingState';

    @Component
    export default class Loader extends Vue {
        private static instance: Loader | null;

        @Prop({type: String, default: ''}) public rosterName!: string;

        @Ref('contentElement') public contentElement!: HTMLDivElement;

        public loaderStates: LoadingStates = {
            roster: LoadingState.NotLoading,
            hourCounter: LoadingState.NotLoading,
            resources: LoadingState.NotLoading,
            workSchedule: LoadingState.NotLoading,
            rosters: LoadingState.NotLoading,
            shifts: LoadingState.NotLoading,
            configuration: LoadingState.NotLoading,
        };

        private loadingCounter: LoadingCounters = {
            roster: 0,
            hourCounter: 0,
            resources: 0,
            workSchedule: 0,
            rosters: 0,
            shifts: 0,
            configuration: 0,
        };

        public manuallyClosed: boolean = false;
        public fadeOut: boolean = false;

        public mounted(): void {
            Loader.instance = this;
        }

        public beforeUnmount(): void {
            Loader.instance = null;
        }

        public setPending(group: LoadingCategory): void {
            this.loaderStates[group] = LoadingState.Pending;
        }

        public setLoading(group: LoadingCategory): void {
            this.addCount(group);
            this.loaderStates[group] = LoadingState.Loading;
        }

        public setLoaded(group: LoadingCategory): void {
            this.subCount(group);
            if (this.loadingCounter[group] === 0 && this.loaderStates[group] !== LoadingState.Error) {
                this.loaderStates[group] = LoadingState.Loaded;
            }
        }

        public setNotLoading(group: LoadingCategory): void {
            this.loadingCounter[group] = 0;
            this.loaderStates[group] = LoadingState.NotLoading;
        }

        public setError(group: LoadingCategory): void {
            this.subCount(group);
            this.loaderStates[group] = LoadingState.Error;
        }

        private addCount(group: LoadingCategory): void {
            this.loadingCounter[group] += 1;
        }

        private subCount(group: LoadingCategory): void {
            this.loadingCounter[group] += -1;
        }

        public isProcessing(group: LoadingCategory): boolean {
            return this.loaderStates[group] !== LoadingState.NotLoading;
        }

        public isLoaded(group: LoadingCategory): boolean {
            if (this.loaderStates[group] === LoadingState.NotLoading) {
                return true;
            }

            return this.loaderStates[group] === LoadingState.Loaded;
        }

        public resetStates(): void {
            let counterKey: LoadingCategory;
            for (counterKey in this.loadingCounter) {
                this.loadingCounter[counterKey] = 0;
            }
            let stateKey: LoadingCategory;
            for (stateKey in this.loaderStates) {
                this.loaderStates[stateKey] = LoadingState.NotLoading;
            }
        }

        public get isAnyLoading(): boolean {
            let i: LoadingCategory;
            for (i in this.loaderStates) {
                if (this.isStateLoading(this.loaderStates[i])) {
                    return true;
                }
            }

            return false;
        }

        public get isAnyProcessing(): boolean {
            let i: LoadingCategory;
            for (i in this.loaderStates) {
                if (this.isStateProcessing(this.loaderStates[i])) {
                    return true;
                }
            }

            return false;
        }

        public get isPageLoaded(): boolean {
            return this.isStateLoaded(this.loaderStates.rosters)
                && this.isStateLoaded(this.loaderStates.shifts)
                && this.isStateLoaded(this.loaderStates.configuration);
        }

        public get isRosterLoaded(): boolean {
            return this.isStateLoaded(this.loaderStates.shifts)
                && this.isStateLoaded(this.loaderStates.configuration)
                && this.isStateLoaded(this.loaderStates.roster)
                && this.isStateLoaded(this.loaderStates.hourCounter)
                && this.isStateLoaded(this.loaderStates.resources)
                && this.isStateLoaded(this.loaderStates.workSchedule);
        }

        public get fullscreen(): boolean {
            if (this.isStateLoading(this.loaderStates.roster)) {
                return true;
            }

            return this.isStateLoading(this.loaderStates.rosters);
        }

        @Watch('isAnyLoading')
        private onIsAnyLoadingChange(value: boolean, oldValue: boolean): void {
            if (this.manuallyClosed && value && !oldValue) {
                this.manuallyClosed = false;
            }
        }

        @Watch('loaderStates', {deep: true})
        private onLoaderStatesChange(): void {
            this.fadeOut = !this.isAnyLoading;
            if (this.manuallyClosed) {
                this.fadeOut = false;
            }
        }

        public transitionEnd(event: TransitionEvent): void {
            if (event.target === this.contentElement && event.propertyName === 'opacity') {
                if (!this.isAnyLoading) {
                    this.resetStates();
                }

                // Need nextTick to ensure watch functions are handled for the loader.resetStates(). This would otherwise reset the fadeOut value to true
                this.$nextTick((): void => {
                    this.fadeOut = false;
                });
            }
        }

        public stateIconClass(state: LoadingState): Record<string, boolean> {
            // keys are icon names from the ui components library
            return {
                spinner: state === LoadingState.Loading, // a spinner
                approved: state === LoadingState.Loaded, // a checkmark
                pending: state === LoadingState.Pending || state === LoadingState.NotLoading, // an hourglass
                close: state === LoadingState.Error, // a cross
            };
        }

        public isStateProcessing(state: LoadingState): boolean {
            return state !== LoadingState.NotLoading;
        }

        public isStateLoading(state: LoadingState): boolean {
            if (!this.isStateProcessing(state)) {
                return false;
            }

            return state !== LoadingState.Loaded;
        }

        public isStateLoaded(state: LoadingState): boolean {
            if (!this.isStateProcessing(state)) {
                return true;
            }

            return state === LoadingState.Loaded;
        }

        public closeModal(): void {
            let i: keyof LoadingStates;
            for (i in this.loaderStates) {
                this.loaderStates[i] = LoadingState.NotLoading;
            }
            this.manuallyClosed = true;
        }

        public static setPending(group: keyof LoadingStates): void {
            this.instance!.setPending(group);
        }

        public static setLoading(group: keyof LoadingStates): void {
            this.instance!.setLoading(group);
        }

        public static setLoaded(group: keyof LoadingStates): void {
            this.instance!.setLoaded(group);
        }

        public static setNotLoading(group: keyof LoadingStates): void {
            this.instance!.setNotLoading(group);
        }

        public static setError(group: keyof LoadingStates): void {
            this.instance!.setError(group);
        }

        public static isProcessing(group: keyof LoadingStates): boolean {
            return this.instance!.isProcessing(group);
        }

        public static isLoaded(group: keyof LoadingStates): boolean {
            return this.instance!.isLoaded(group);
        }

        public static resetStates(): void {
            this.instance!.resetStates();
        }
    }
</script>

<style lang="scss" scoped>
    #loader {
        --fadeOutDuration: 1.5s;
        --animationDuration: .3s;
        --fadeOutDelay: 2s;
        display: none;
        z-index: 1000000;
        height: 0;
        width: 0;

        &.visible {
            display: block;
        }

        &.fade-out {
            display: block;

            .content {
                transition-delay: var(--fadeOutDelay), 0s, 0s, 0s;
                opacity: 0;
            }
        }
    }

    .backdrop {
        position: fixed;
        width: 0;
        height: 0;
        top: 0;
        left: 0;
        background: transparent;
        transition-property: background, width, height;
        transition-duration: var(--animationDuration), 0s, 0s;
        transition-delay: 0s, var(--animationDuration), var(--animationDuration);

        &.fullscreen {
            width: 100vw;
            height: 100vh;
            background: rgb(0 0 0 / 0.5);
        }
    }

    .content {
        position: fixed;
        overflow: hidden;
        background: var(--modal-background);
        color: var(--theme-text-color);
        width: 20rem;
        bottom: 1rem;
        right: 1rem;
        border-radius: 3px;
        opacity: 1;
        transition: opacity var(--animationDuration) linear 0s, bottom var(--animationDuration) linear 0s, right var(--animationDuration) linear 0s, transform var(--animationDuration) linear 0s;
        transform: translate(0, 0) scale(1);
        box-shadow: var(--generic-shadow-average);

        &.fullscreen {
            bottom: 50vh;
            right: 50vw;
            transform: translate(50%, 50%) scale(1.25);

            .closeButton {
                display: none;
            }
        }

        .closeButton {
            position: absolute;
            top: .5rem;
            right: .5rem;
            color: #fff;
            cursor: pointer;
        }
    }

    .header {
        background: var(--header-bar-background-color);
        color: var(--header-bar-text-color);
        font-size: 1.125rem;
        padding: .25rem .5rem;
    }

    %loaded-sections-hidden {
        margin: 0;
        max-height: 0;
        overflow: hidden;
    }

    .page-loaded {
        .rosters, .shifts, .configuration {
            @extend %loaded-sections-hidden;
        }
    }

    .roster-loaded {
        .roster, .hour-counter, .position-assignment {
            @extend %loaded-sections-hidden;
        }
    }

    .row {
        margin: .25rem 0;
        padding: 0 .75rem;
        max-height: 5rem;
        display: flex;
        align-items: center;
        transition: max-height var(--fadeOutDuration) ease-out var(--fadeOutDelay), margin var(--fadeOutDuration) ease-out var(--fadeOutDelay);
    }

    .icon {
        position: relative;
        margin: 0 .25rem 0 0;

        &.approved {
            background-color: var(--loader-success-color);
        }

        &.close {
            background-color: var(--loader-error-color);
        }
    }
</style>
